import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { ClipboardModule } from '@angular/cdk/clipboard';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { LayoutModule } from '@angular/cdk/layout';
import { OverlayModule } from '@angular/cdk/overlay';
import { PortalModule } from '@angular/cdk/portal';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatBottomSheetModule } from '@angular/material/bottom-sheet';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatRippleModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import {
  MatAutocompleteModule
} from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import {
  MatFormFieldModule
} from '@angular/material/form-field';
// Material
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule, MAT_PAGINATOR_DEFAULT_OPTIONS } from '@angular/material/paginator';
import {
  MatProgressBarModule
} from '@angular/material/progress-bar';
import {
  MatProgressSpinnerModule
} from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import {
  MatSlideToggleModule
} from '@angular/material/slide-toggle';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
// Vendor
import { NgSelectModule } from '@ng-select/ng-select';

import { NgxEditorModule } from 'ngx-editor';
import { VirtualScrollerModule } from '@iharbeck/ngx-virtual-scroller';

import { FeatureFlagsModule } from 'app/feature-flags/feature-flags.module';
import { AttachDialogComponent } from 'shared/components/attach-dialog/attach-dialog-component';
import { ConfirmDialogComponent } from 'shared/components/confirm-dialog/confirm-dialog-component';
import {
  ErrorNotificationComponent
} from 'shared/components/error-notification/error-notification.component';
import { FullAddressComponent } from 'shared/components/full-address.component';
import { InputErrorComponent } from 'shared/components/input-error/input-error.component';
// Shared
import { SpinnerComponent } from 'shared/components/spinner.component';
import { SuccessNotificationComponent } from 'shared/components/success-notification.component';
import { UpdateGeocodeComponent } from 'shared/components/update-geocode/update-geocode.component';
import {
  UpdateGeocodeDialogComponent
} from 'shared/components/update-geocode/update-geocode.dialog';
import { DateMaskDirective } from 'shared/directives/date-mask.directive';
import { DragAndDropDirective } from 'shared/directives/drag-and-drop.directive';
import { AutofocusDirective } from 'shared/directives/focus.directive';
import { HideDirective } from 'shared/directives/hide.directive';
import { IfInRoleDirective } from 'shared/directives/if-in-role.directive';
import { IfMinRoleDirective } from 'shared/directives/if-min-role.directive';
import {
  MatSelectFilterExtensionDirective
} from 'shared/directives/mat-select-filter-extention.directive';
import {
  MatSelectedClassExtensionDirective
} from 'shared/directives/mat-selected-class-extension.directive';
import { AddressPipe } from 'shared/pipes/address.pipe';
import { AgePipe } from 'shared/pipes/age.pipe';
import { EventEndTimePipe } from 'shared/pipes/event-end-time.pipe';
import { EventStartEndTimePipe } from 'shared/pipes/event-start-end-time.pipe';
import { FullNamePipe } from 'shared/pipes/full-name.pipe';
import { InitialsPipe } from 'shared/pipes/initials.pipe';
import { PhonePipe } from 'shared/pipes/phone.pipe';
import { PropercasePipe } from 'shared/pipes/propercase.pipe';
import { RelativeDatePipe } from 'shared/pipes/relative-date.pipe';
import { SafeUrlPipe } from 'shared/pipes/safe-url.pipe';
import { StatusPipe } from 'shared/pipes/status.pipe';
import * as Tooltip from 'shared/tooltip';

import {
  AttachmentButtonComponent
} from './components/attachment-button/attachment-button.component';
import {
  CampaignQuickSearchComponent
} from './components/campaign-select2/campaign-quicksearch.component';
import { CampaignSelect2Component } from './components/campaign-select2/campaign-select2.component';
import {
  CampaignVisibilityFilterComponent
} from './components/campaign-visiblity-filter/campaign-visiblity-filter.component';
import { ClippedNoteComponent } from './components/clipped-note/clipped-note.component';
import { DayTimeInputComponent } from './components/day-time-input/day-time-input.component';
import { FileSelectorComponent } from './components/file-selector/file-selector.component';
import {
  GraphQLInfiniteScrollSearchComponent
} from './components/graphql-infinite-scroll-search/graphql-infinite-scroll-search.component';
import {
  NoResultsTmplDirective
} from './components/graphql-infinite-scroll-search/no-results-tmpl.directive';
import {
  ResultTmplDirective
} from './components/graphql-infinite-scroll-search/results-temp.directive';
import {
  IconSelectInputComponent
} from './components/icon-select-input/icon-select-input.component';
import { ListItemComponent } from './components/list-item/list-item.component';
import { MatPhoneInputComponent } from './components/mat-phone-input/mat-phone-input.component';
import { PhoneFieldComponent } from './components/phone-field/phone-field.component';
import { ProgressCircleComponent } from './components/progress-circle/progress-circle.component';
import {
  RangeOrRelativeDateInputComponent
} from './components/range-or-relative-date-input/range-or-relative-date-input.component';
import {
  RelativeDateInputComponent
} from './components/relative-date-input/relative-date-input.component';
import { SortControlComponent } from './components/sort-control/sort-control.component';
import { TypeNameLabelComponent } from './components/type-name-label.component';
import { WallNavComponent } from './components/wall-nav/wall-nav.component';
import { WysiwygInputComponent } from './components/wysiwyg-input/wysiwyg-input.component';
import { LanguageListSummaryComponent } from './components/language-list-summary/language-list-summary.component';
import { AccordianExpansionDirective } from './directives/accordion-expansion.directive';
import { AutocompleteOffDirective } from './directives/autocomplete-off.directive';
import { ButtonSpinnerDirective } from './directives/button-spinner.directive';
import { DecimalMaskDirective } from './directives/decimal-mask.directive';
import { IfOrAdminDirective } from './directives/if-or-admin.directive';
import { OutsideClickDirective } from './directives/outside-click.directive';
import { ScrollIntoViewDirective } from './directives/scroll-into-view.directive';
import { StopClickBubbleDirective } from './directives/stop-click-bubble.directive';
import { AddressFormComponent } from './forms/address-form/address-form.component';
import { BaseModalComponent } from './modal.component';
import { AorAnPipe } from './pipes/aoran.pipe';
import { AutolinkerPipe } from './pipes/autolinker.pipe';
import { BestPipe } from './pipes/best.pipe';
import { CityStatePipe } from './pipes/city-state.pipe';
import { CountryLongPipe } from './pipes/country-long.pipe';
import { EscapedHtmlPipe } from './pipes/escaped-html.pipe';
import { FileSizePipe } from './pipes/filesize.pipe';
import { HighlightTextPipe } from './pipes/highlight-text.pipe';
import { LanguageNamePipe } from './pipes/language-name.pipe';
import { LimitedCountToTextPipe } from './pipes/limted-count-to-text.pipe';
import { SafeHtmlPipe } from './pipes/safe-html.pipe';
import { SanitizedStyledHtmlPipe } from './pipes/sanitized-styled-html';
import { SourcePipe } from './pipes/source.pipe';
import { StripHtmlPipe } from './pipes/strip-html.pipe';
import { TagSelectLabelPipe } from './pipes/tag-select-label.pipe';
import { TruncateMiddlePipe } from './pipes/truncate-middle.pipe';
import { TruncateTextPipe } from './pipes/truncate-text.pipe';
import { UserListAutocompleteComponent } from './components/user-list-autocomplete/user-list-autocomplete.component';
import { TimeFormatPipe } from './pipes/time-format.pipe';
import { TimezonePipe } from './pipes/timezone';
import { LongPressDirective } from './directives/long-press.directive';
import { ActivityDateTimePipe } from './pipes/activity-date.pipe';

const MATERIAL_MODULES = [
  MatInputModule,
  MatChipsModule,
  MatButtonModule,
  MatButtonToggleModule,
  MatBottomSheetModule,
  MatSnackBarModule,
  MatFormFieldModule,
  MatSlideToggleModule,
  MatProgressBarModule,
  MatCheckboxModule,
  MatCardModule,
  MatDialogModule,
  MatProgressSpinnerModule,
  MatListModule,
  MatMenuModule,
  MatIconModule,
  MatExpansionModule,
  MatDatepickerModule,
  MatAutocompleteModule,
  MatRadioModule,
  MatPaginatorModule,
  MatTooltipModule,
  MatTabsModule,
  MatSelectModule,
  MatRippleModule,
  ScrollingModule,
  DragDropModule,
  OverlayModule,
  PortalModule,
  ClipboardModule
];

const EXTERNAL_MODULES = [
  ...MATERIAL_MODULES,
  NgSelectModule,
  NgxEditorModule,
  NgxMatSelectSearchModule
];

const PIPES = [
  ActivityDateTimePipe,
  AddressPipe,
  AgePipe,
  AutolinkerPipe,
  AorAnPipe,
  BestPipe,
  CityStatePipe,
  EscapedHtmlPipe,
  EventEndTimePipe,
  EventStartEndTimePipe,
  FileSizePipe,
  FullNamePipe,
  InitialsPipe,
  PhonePipe,
  PropercasePipe,
  SafeUrlPipe,
  HighlightTextPipe,
  RelativeDatePipe,
  StatusPipe,
  LanguageNamePipe,
  TagSelectLabelPipe,
  SafeHtmlPipe,
  CountryLongPipe,
  TruncateTextPipe,
  TruncateMiddlePipe,
  LimitedCountToTextPipe,
  StripHtmlPipe,
  SanitizedStyledHtmlPipe,
  SourcePipe,
  TimeFormatPipe,
  TimezonePipe
];

const DIRECTIVES = [
  AccordianExpansionDirective,
  StopClickBubbleDirective,
  OutsideClickDirective,
  DragAndDropDirective,
  ButtonSpinnerDirective,
  IfOrAdminDirective,
  IfInRoleDirective,
  IfMinRoleDirective,
  HideDirective,
  LongPressDirective,
  DateMaskDirective,
  DecimalMaskDirective,
  AutocompleteOffDirective,
  MatSelectedClassExtensionDirective,
  MatSelectFilterExtensionDirective,
  Tooltip.TooltipDirective,
  NoResultsTmplDirective,
  ResultTmplDirective,
  ScrollIntoViewDirective
];

const COMPONENTS = [
  UserListAutocompleteComponent,
  AttachmentButtonComponent,
  GraphQLInfiniteScrollSearchComponent,
  IconSelectInputComponent,
  FileSelectorComponent,
  RangeOrRelativeDateInputComponent,
  RelativeDateInputComponent,
  ClippedNoteComponent,
  MatPhoneInputComponent,
  PhoneFieldComponent,
  TypeNameLabelComponent,
  DayTimeInputComponent,
  WallNavComponent,
  CampaignQuickSearchComponent,
  CampaignVisibilityFilterComponent,
  CampaignSelect2Component,
  ListItemComponent,
  SortControlComponent,
  DragAndDropDirective,
  ConfirmDialogComponent,
  AttachDialogComponent,
  FullAddressComponent,
  InputErrorComponent,
  AutofocusDirective,
  ErrorNotificationComponent,
  SuccessNotificationComponent,
  Tooltip.TooltipContentComponent,
  SpinnerComponent,
  UpdateGeocodeDialogComponent,
  UpdateGeocodeComponent,
  ProgressCircleComponent,
  BaseModalComponent,
  WysiwygInputComponent,
  LanguageListSummaryComponent
];

const FORMS = [
  AddressFormComponent
];

@NgModule({
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    ReactiveFormsModule,
    FeatureFlagsModule,
    LayoutModule,
    ...EXTERNAL_MODULES
  ],
  declarations: [
    ...FORMS,
    // Pipes
    ...PIPES,
    // Components
    ...COMPONENTS,
    // Directives
    ...DIRECTIVES
  ],
  providers: [
    { provide: MAT_PAGINATOR_DEFAULT_OPTIONS, useValue: { formFieldAppearance: 'fill' } }
  ],
  exports: [
    VirtualScrollerModule,
    ClipboardModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    LayoutModule,
    FeatureFlagsModule,
    // Externals
    ...EXTERNAL_MODULES,
    // Pipes
    ...PIPES,
    // Components
    ...COMPONENTS,
    ...FORMS,
    // Directives
    ...DIRECTIVES
  ]
})
export class SharedModule {
  /**
   *
   */
  constructor() {
  }
}
